/* eslint-disable @typescript-eslint/naming-convention */
import superagent from "superagent";
import railsCsrf from "superagent-rails-csrf";

import * as Routes from "../generated/routes";
import { defaultLocale, createIntl } from "../locales";

import { logException } from "./log_exception";
import type { RelayId } from "./relay";

const intl = createIntl();

declare module "superagent" {
  interface Request {
    setCsrfToken(): this;
  }
}

railsCsrf(superagent);

const requestErrorMessage = (error, defaultMessage) => {
  if (error.response?.body?.message) {
    return error.response.body.message;
  }
  if (error.response?.body?.errors) {
    return error.response.body.errors.map((e) => e.message || e).join("\n");
  }
  return defaultMessage;
};

function request(createRequestCallback, defaultErrorMessageKey) {
  if (process.env.NODE_ENV !== "production") {
    if (!defaultErrorMessageKey || !intl.formatMessage({ id: defaultErrorMessageKey })) {
      throw new Error(`[DEBUG] No message found for ${defaultErrorMessageKey}`);
    }
  }
  return new Promise<any>((resolve, reject) => {
    createRequestCallback()
      .set("Accept", "application/json")
      .set("X-Requested-With", "XMLHttpRequest")
      .setCsrfToken()
      .end((error, response) => {
        if (error) {
          logException(error);
          reject({ error, message: requestErrorMessage(error, intl.formatMessage({ id: defaultErrorMessageKey })) });
        } else {
          resolve(response.body != null ? response.body : response.text);
        }
      });
  });
}

export interface GraphqlError {
  readonly message: string;

  readonly extensions: any;
}

export interface GraphqlResponse {
  readonly data?: any;
  readonly errors?: Array<GraphqlError>;
}

class KibelaClient {
  previewContent(content, trusted) {
    return request(() => {
      return superagent.post(Routes.previewPath()).send({ content, trusted });
    }, "kibelaClient.showingPreviewFailed");
  }

  putBlogConvertToWiki(id) {
    return request(() => {
      return superagent.put(Routes.blogConvertToWikiPath(id));
    }, "kibelaClient.changeToCoeditingFailed");
  }

  uploadFile(file: Blob, filename?: string, kind?: string) {
    return request(() => {
      const s = superagent.post(Routes.attachmentsPath()).attach("attachment[asset]", file, filename);
      if (kind) {
        s.field("attachment[kind]", kind);
      }
      return s;
    }, "kibelaClient.uploadingFileFailed");
  }

  updateAttachment(key: string, file: Blob, filename?: string) {
    return request(() => {
      return superagent.patch(Routes.attachmentPath(key)).attach("attachment[asset]", file, filename);
    }, "kibelaClient.updatingAttachmentFailed");
  }

  getEditorMentions() {
    return new Promise<Array<any>>((resolve, reject) => {
      superagent
        .get(Routes.editorMentionsPath())
        .set("X-Requested-With", "XMLHttpRequest")
        .end((err, res) => {
          if (err) {
            logException(err);
            reject({
              error: err,
              message: requestErrorMessage(err, intl.formatMessage({ id: "kibelaClient.requestFailed" })),
            });
          } else {
            resolve(res.body);
          }
        });
    });
  }

  getEditorCodeLabels() {
    return new Promise<Array<any>>((resolve, reject) => {
      superagent
        .get(Routes.editorCodeLabelsPath())
        .set("X-Requested-With", "XMLHttpRequest")
        .end((err, res) => {
          if (err) {
            logException(err);
            reject({
              error: err,
              message: requestErrorMessage(err, intl.formatMessage({ id: "kibelaClient.requestFailed" })),
            });
          } else {
            resolve(res.body);
          }
        });
    });
  }

  updateUserOnRegistration({ user, avatar }) {
    return request(() => {
      const req = superagent.patch(Routes.registrationUserPath());
      Object.keys(user).forEach((key) => {
        if (user[key]) {
          req.field(`user[${key}]`, user[key]);
        }
      });
      return req.attach("avatar_photo[asset]", avatar.asset, avatar.fileName);
    }, "kibelaClient.updatingUserOnSignUpFailed");
  }

  updateTeamSetting(
    organizationKind,
    organizationName = "",
    departmentName = "",
    phoneNumber = "",
    postalCode = "",
    country = "",
    state = "",
    city = "",
    streetAddress = "",
  ) {
    return request(() => {
      return superagent.patch(Routes.registrationTeamSettingPath()).send({
        team_setting: {
          organization_kind: organizationKind,
          organization_name: organizationName,
          department_name: departmentName,
          phone_number: phoneNumber,
          postal_code: postalCode,
          country,
          state,
          city,
          street_address: streetAddress,
        },
      });
    }, "kibelaClient.requestFailed");
  }

  inviteOnRegistration({ emails }) {
    return request(() => {
      return superagent.post(Routes.registrationInvitationsPath()).send({ emails });
    }, "kibelaClient.postingInvitationEmailsOnSignUpFailed");
  }

  getStatusOnRegistration() {
    return request(() => {
      return superagent.get(Routes.registrationStatusPath());
    }, "kibelaClient.checkWaitingOnSignUpFailed");
  }

  updateUserRole(id, role) {
    return new Promise((resolve, reject) => {
      superagent
        .put(Routes.userPath(id))
        .setCsrfToken()
        .set("X-Requested-With", "XMLHttpRequest")
        .send({ role })
        .end((err, res) => {
          if (err) {
            logException(err);
            reject({
              error: err,
              message: requestErrorMessage(err, intl.formatMessage({ id: "kibelaClient.changingRoleFailed" })),
            });
          } else {
            resolve(res.body);
          }
        });
    });
  }

  updateUserIntroduction(board_creating_completed) {
    return new Promise((resolve, reject) => {
      superagent
        .patch(Routes.userIntroductionPath())
        .setCsrfToken()
        .set("X-Requested-With", "XMLHttpRequest")
        .send({ introduction: { board_creating_completed } })
        .end((err, res) => {
          if (err) {
            logException(err);
            reject({ error: err, message: requestErrorMessage(err, "") });
          } else {
            resolve(res.body);
          }
        });
    });
  }

  updateNotificationsSettings(notification_type, enabled) {
    const params = {};
    params[notification_type] = enabled ? "1" : "0";
    return request(() => {
      return superagent.patch(Routes.notificationsSettingsPath()).send(params);
    }, "kibelaClient.requestFailed");
  }

  deleteCoverImage() {
    return request(() => {
      return superagent.del(Routes.coverImageSettingsPath()).set("Accept", "application/json");
    }, "kibelaClient.deletingFailed");
  }

  deleteAvatar() {
    return request(() => {
      return superagent.del(Routes.avatarSettingsPath()).set("Accept", "application/json");
    }, "kibelaClient.deletingFailed");
  }

  uploadAvatar(avatarId, blob, filename) {
    return request(() => {
      const req = superagent.post(Routes.avatarSettingsPath()).attach("avatar[asset]", blob, filename);
      if (avatarId) {
        return req.field("avatar[id]", avatarId);
      } else {
        return req;
      }
    }, "kibelaClient.uploadingFileFailed");
  }

  createBillingMethods(stripeToken) {
    return request(() => {
      return superagent.post(Routes.teamBillingMethodsPath()).send({ stripe_token: stripeToken });
    }, "kibelaClient.createBillingMethodsFailed");
  }

  changePlan(planId) {
    return request(() => {
      const data: any = { plan_id: planId };
      return superagent.patch(Routes.teamBillingSubscriptionPath()).send(data);
    }, "kibelaClient.changePlan");
  }

  register(email: string, initialPlanName: string) {
    return request(() => {
      return superagent
        .post(Routes.registerPath({ locale: defaultLocale }))
        .send({ email, initial_plan_name: initialPlanName });
    }, "kibelaClient.registerFailed");
  }

  getBoardUsers(boardId: RelayId, { page, per, all = false }) {
    return request(() => {
      return superagent.get(Routes.boardUsersPath(boardId, { page, per, all }));
    }, "kibelaClient.requestFailed");
  }

  searchBoardUsers(boardId: RelayId, { query, page, per, all = false }) {
    return request(() => {
      return superagent.get(Routes.searchBoardUsersPath(boardId, { query, page, per, all }));
    }, "kibelaClient.requestFailed");
  }

  deleteTeam(reason, questionnaire) {
    const data: any = { reason };
    if (questionnaire) {
      data.questionnaire = questionnaire;
    }
    return request(() => {
      return superagent.del(Routes.teamDisablePath()).send(data);
    }, "kibelaClient.requestFailed");
  }

  deleteTeamLoggedIn(subdomain: string) {
    return request(() => {
      return superagent.del(Routes.teamLoggedInPath()).send({ subdomain });
    }, "kibelaClient.requestFailed");
  }

  /**
   * @deprecated Use kibelaClient2 instead.
   */
  graphql(params: { query: string; variables?: Record<string, any> }) {
    return new Promise<GraphqlResponse>((resolve, reject) => {
      superagent
        .post(Routes.apiInternalPath())
        .send(params)
        .set("X-Requested-With", "XMLHttpRequest")
        .end((error, response) => {
          if (process.env.NODE_ENV !== "production") {
            console.log(
              "GraphQL:",
              { ...params, query: params.query.replace(/\n/g, "") },
              response ? response.body : undefined,
            );
          }
          if (error) {
            logException(error);
            reject({
              error,
              message: requestErrorMessage(error, intl.formatMessage({ id: "kibelaClient.requestFailed" })),
            });
          } else {
            resolve(response.body);
          }
        });
    });
  }

  updateTeam(subdomain) {
    return request(() => {
      return superagent.put(Routes.teamManagePath()).send({ subdomain });
    }, "kibelaClient.requestFailed");
  }

  createTotpSecret(secret: string, attempt: string) {
    return request(() => {
      return superagent.post(Routes.multiFactorAuthnSettingsPath()).send({ secret, attempt });
    }, "kibelaClient.requestFailed");
  }

  deleteTotpSecret() {
    return request(() => {
      return superagent.del(Routes.multiFactorAuthnSettingsPath());
    }, "kibelaClient.requestFailed");
  }

  createRecoveryCodes() {
    return request(() => {
      return superagent.post(Routes.createRecoveryCodeMultiFactorAuthnSettingsPath());
    }, "kibelaClient.requestFailed");
  }

  updateMfaEnforcing(mfaEnforcingState: "mfa_not_enforced" | "mfa_enforcing_transition_period" | "mfa_enforced") {
    return request(() => {
      return superagent.patch(Routes.teamSettingMultiFactorAuthnPath()).send({ mfaEnforcingState });
    }, "kibelaClient.requestFailed");
  }

  updateGroupManageRestriction(
    groupManageRestriction: "group_manageable_by_full_member_and_upper" | "group_manageable_by_admin_and_owner",
  ) {
    return request(() => {
      return superagent.patch(Routes.teamSettingGroupManagePath()).send({ groupManageRestriction });
    }, "kibelaClient.requestFailed");
  }
}

export const kibelaClient = new KibelaClient(); // singleton
